import React from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBracketsCurly,
  faCalculator,
  faDiagramNested,
  faFile,
  faMagnifyingGlass,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import type { ToolType } from "src/clients/agent-management";
import { Icon, IconVariant } from "src/design-system/Icon";

type ToolCardProps = {
  name: string;
  description?: string;
  type: ToolType;
  onClick?: () => void;
  onDelete?: () => void;
  rightSlot?: React.ReactNode;
  /** Rendered inline next to the title, e.g. an info tooltip trigger. */
  titleAdornment?: React.ReactNode;
  selected?: boolean;
  dataLoc?: string;
};

type ToolIconConfig = {
  icon: IconDefinition;
  bgColor: string;
  iconVariant: IconVariant;
};

// To display a new tool type in the UI, just add a new entry here.
// Presence in this map = the tool type is displayable.
export const TOOL_ICON_CONFIG: Partial<Record<ToolType, ToolIconConfig>> = {
  flow: {
    icon: faDiagramNested,
    bgColor: "bg-neo-green-200",
    iconVariant: "hover",
  },
  read_file: {
    icon: faFile,
    bgColor: "bg-neo-green-200",
    iconVariant: "hover",
  },
  calculator: {
    icon: faCalculator,
    bgColor: "bg-neo-green-200",
    iconVariant: "hover",
  },
  web_search: {
    icon: faMagnifyingGlass,
    bgColor: "bg-neo-green-200",
    iconVariant: "hover",
  },
  structured_output: {
    icon: faBracketsCurly,
    bgColor: "bg-neo-green-200",
    iconVariant: "hover",
  },
};

export const ToolCard: React.FC<ToolCardProps> = ({
  name,
  description,
  type,
  onClick,
  onDelete,
  rightSlot,
  titleAdornment,
  selected,
  dataLoc,
}) => {
  const iconConfig = TOOL_ICON_CONFIG[type];

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  if (!iconConfig) return null;

  return (
    <div
      className={twJoin(
        "group flex items-center gap-3 rounded-lg border bg-white p-2",
        selected ? "border-indigo-500" : "border-gray-200",
        onClick && !selected && "cursor-pointer hover:border-gray-300",
      )}
      data-loc={dataLoc}
      onClick={onClick}
    >
      <div
        className={twJoin(
          "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
          iconConfig.bgColor,
        )}
      >
        <Icon
          variant={iconConfig.iconVariant}
          icon={iconConfig.icon}
          size="sm"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="min-w-0 truncate font-inter-semibold-13px text-gray-800">
            {name}
          </p>
          {titleAdornment && (
            <div className="flex-shrink-0">{titleAdornment}</div>
          )}
        </div>
        {description && (
          <p className="line-clamp-2 font-inter-normal-12px text-gray-500">
            {description}
          </p>
        )}
      </div>
      {rightSlot}
      {onDelete && (
        <button
          className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          data-loc={`${dataLoc}-delete`}
          type="button"
          onClick={handleDelete}
        >
          <Icon icon={faTrashCan} size="sm" />
        </button>
      )}
    </div>
  );
};
